import React, { useState, useEffect } from 'react';
import { Download, Printer, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function WarrantyNumbersList({ numbers }) {
  const [expanded, setExpanded] = useState(false);
  if (!numbers || numbers.length === 0) return <span>-</span>;
  if (numbers.length <= 3) return <span>{numbers.join(', ')}</span>;
  return (
    <div>
      <span>{expanded ? numbers.join(', ') : `${numbers.slice(0, 3).join(', ')}...`}</span>
      <button onClick={() => setExpanded(!expanded)} className="ml-2 text-emerald-700 hover:text-emerald-900 text-xs font-medium">
        {expanded ? <><EyeOff size={12} className="inline" /> Skjul</> : <><Eye size={12} className="inline" /> Vis alle ({numbers.length})</>}
      </button>
    </div>
  );
}

export default function KitOrdersTab({ kitOrders, loading, auth, dealers, loadDealers, loadKitOrders }) {
  const [generating, setGenerating] = useState(false);
  const [genMessage, setGenMessage] = useState('');
  const [assignModal, setAssignModal] = useState(null);
  const [selectedDealer, setSelectedDealer] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => { if (dealers.length === 0 && loadDealers) loadDealers(); }, [dealers.length, loadDealers]);

  if (loading) return <div className="text-stone-500">Laster...</div>;
  const typeLabels = { test: 'Test', purchase: 'Forhandler', standard: 'Standard (10)', 'prøve': 'Prøve (2)', diy: 'DIY' };
  const typeColors = { test: 'bg-blue-100 text-blue-800', purchase: 'bg-emerald-100 text-emerald-800', standard: 'bg-emerald-100 text-emerald-800', 'prøve': 'bg-amber-100 text-amber-800', diy: 'bg-purple-100 text-purple-800' };

  const downloadPdf = async (orderId, orderNumber) => {
    try {
      const res = await fetch(`${API}/admin/warranty-numbers-pdf/${orderId}`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `garantinumre_${orderNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* ignore */ }
  };

  const printLabels = async (orderId) => {
    try {
      const res = await fetch(`${API}/admin/kit-orders/${orderId}/labels`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (!res.ok) return;
      const data = await res.json();
      const labels = (data.labels && data.labels.length)
        ? data.labels
        : (data.warranty_numbers || []).map(n => ({ warranty_number: n, url: '', qr_data_url: '' }));

      const printWin = window.open('', '_blank');
      printWin.document.write(`<!DOCTYPE html><html><head><title>Etiketter - ${data.box_number}</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
          .label-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; }
          .label { border: 1px dashed #ccc; padding: 6mm 4mm; text-align: center; border-radius: 4px; page-break-inside: avoid; }
          .label-box { background: #f5f5f4; border: 2px solid #292524; padding: 10mm 8mm; text-align: center; border-radius: 6px; margin-bottom: 12mm; }
          .label-box .number { font-size: 28px; font-weight: bold; letter-spacing: 2px; }
          .label-box .sub { font-size: 12px; color: #78716c; margin-top: 4px; }
          .label .qr { display: block; margin: 0 auto 4mm auto; width: 28mm; height: 28mm; }
          .label .number { font-size: 14px; font-weight: bold; letter-spacing: 1px; }
          .label .type { font-size: 9px; color: #78716c; margin-top: 2px; }
          .label .scan-hint { font-size: 8px; color: #78716c; margin-top: 1mm; }
          h2 { margin: 0 0 8mm 0; font-size: 14px; color: #78716c; }
          @media print { .no-print { display: none; } }
        </style></head><body>
        <button class="no-print" onclick="window.print()" style="margin-bottom:16px;padding:8px 24px;font-size:16px;cursor:pointer;background:#064e3b;color:white;border:none;border-radius:8px;">Skriv ut etiketter</button>
        <div class="label-box">
          <div class="number">${data.box_number}</div>
          <div class="sub">ESKE - ${labels.length} kit${data.dealer_name ? ' - ' + data.dealer_name : ''}</div>
        </div>
        <h2>Kit-etiketter (garantinumre med QR-kode)</h2>
        <div class="label-grid">
          ${labels.map(l => `
            <div class="label">
              ${l.qr_data_url ? `<img class="qr" src="${l.qr_data_url}" alt="QR ${l.warranty_number}" />` : ''}
              <div class="number">${l.warranty_number}</div>
              <div class="type">DC-Protect Garanti</div>
              <div class="scan-hint">Skann for å registrere</div>
            </div>
          `).join('')}
        </div>
      </body></html>`);
      printWin.document.close();
    } catch { /* ignore */ }
  };

  const generateTestBatch = async () => {
    if (!window.confirm('Generere 500 prøve-garantier (250 esker med 2 kit)? Dette kan ta litt tid.')) return;
    setGenerating(true);
    setGenMessage('Genererer...');
    try {
      const res = await fetch(`${API}/admin/generate-test-warranties`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 500 })
      });
      if (res.ok) {
        const data = await res.json();
        setGenMessage(`Ferdig! ${data.total_warranties} garantinumre generert i ${data.total_boxes} esker.`);
        if (loadKitOrders) loadKitOrders();
      } else {
        const err = await res.json().catch(() => ({}));
        setGenMessage(err.detail || 'Kunne ikke generere esker. Sjekk at alle felter er fylt ut.');
      }
    } catch { setGenMessage('Kunne ikke koble til serveren.'); }
    finally { setGenerating(false); }
  };

  const assignDealer = async (orderId) => {
    if (!selectedDealer) return;
    setAssigning(true);
    try {
      const res = await fetch(`${API}/admin/kit-orders/${orderId}/assign-dealer`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealer_id: selectedDealer })
      });
      if (res.ok) {
        setAssignModal(null);
        setSelectedDealer('');
        if (loadKitOrders) loadKitOrders();
      }
    } catch { /* ignore */ }
    finally { setAssigning(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/warranty-numbers/export`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'garantinumre.csv';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* ignore */ }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-stone-900">Kit-bestillinger ({kitOrders.length})</h2>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="bg-stone-200 text-stone-700 hover:bg-stone-300 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-testid="export-csv-btn">
            <Download size={14} className="inline mr-1" /> Eksporter CSV
          </button>
          <button onClick={generateTestBatch} disabled={generating} className="bg-amber-600 text-white hover:bg-amber-700 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50" data-testid="generate-test-batch-btn">
            {generating ? 'Genererer...' : 'Generer 500 prøve-garantier'}
          </button>
        </div>
      </div>
      {genMessage && <p className={`text-sm mb-4 ${genMessage.includes('Ferdig') ? 'text-emerald-600' : genMessage.includes('galt') ? 'text-red-600' : 'text-stone-500'}`}>{genMessage}</p>}
      {kitOrders.length === 0 ? (
        <p className="text-stone-500">Ingen kit-bestillinger.</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-stone-200" data-testid="kit-orders-table">
            <thead className="bg-stone-50">
              <tr>
                {['Eskenr', 'Forhandler', 'Type', 'Antall', 'Garantinumre', 'Dato', 'Handlinger'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {kitOrders.map((o) => (
                <tr key={o.id} className="hover:bg-stone-50">
                  <td className="px-4 py-3 text-sm font-bold text-stone-900">{o.box_number || o.order_number}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">
                    {o.dealer_name || <span className="text-amber-600 font-medium cursor-pointer hover:underline" onClick={() => { setAssignModal(o); setSelectedDealer(''); }} data-testid={`assign-dealer-${o.id}`}>Ikke tilordnet</span>}
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${typeColors[o.kit_type] || 'bg-stone-100 text-stone-800'}`}>{typeLabels[o.kit_type] || o.kit_type}</span></td>
                  <td className="px-4 py-3 text-sm text-stone-600">{o.kit_quantity}</td>
                  <td className="px-4 py-3 text-sm text-stone-500 max-w-xs">
                    <WarrantyNumbersList numbers={o.warranty_numbers} />
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">{new Date(o.order_date).toLocaleDateString('no-NO')}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => printLabels(o.id)} className="inline-flex items-center gap-1 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-2 py-1 rounded-lg transition-colors" data-testid={`print-labels-${o.id}`}>
                        <Printer size={12} /> Etiketter
                      </button>
                      <button onClick={() => downloadPdf(o.id, o.order_number)} className="inline-flex items-center gap-1 text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 px-2 py-1 rounded-lg transition-colors" data-testid={`pdf-download-${o.id}`}>
                        <Download size={12} /> PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign dealer modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="assign-dealer-modal">
            <h3 className="text-lg font-semibold text-stone-900 mb-4">Tilordne forhandler til eske {assignModal.box_number || assignModal.order_number}</h3>
            <p className="text-sm text-stone-500 mb-4">Garantinumre: {assignModal.warranty_numbers?.join(', ')}</p>
            <select value={selectedDealer} onChange={(e) => setSelectedDealer(e.target.value)} className="w-full px-4 py-3 border border-stone-300 rounded-lg mb-4 focus:ring-2 focus:ring-emerald-800 focus:border-transparent">
              <option value="">Velg forhandler...</option>
              {dealers.map((d) => <option key={d.id} value={d.id}>{d.company_name} - {d.contact_person}</option>)}
            </select>
            <div className="flex gap-3">
              <button disabled={!selectedDealer || assigning} onClick={() => assignDealer(assignModal.id)} className="flex-1 bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-4 py-2 font-medium transition-colors disabled:opacity-50" data-testid="confirm-assign-btn">
                {assigning ? 'Tilordner...' : 'Tilordne'}
              </button>
              <button onClick={() => setAssignModal(null)} className="flex-1 bg-stone-200 text-stone-700 hover:bg-stone-300 rounded-lg px-4 py-2 font-medium transition-colors">Avbryt</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
